<ng-template #parameterTrailingMenuBar>
  <div class="flex items-center gap-2 h-full">
    <soft-ds-button
      variant="outline"
      size="md"
      mode="iconOnly"
      [title]="('#PMS_ROSTER.REFRESH' | translate) ?? ''"
      (click)="service.refreshEvents()"
      data-testid="softline-roster-refresh"
    >
      <ng-template softSlot="leading">
        <soft-ds-icon name="fa-regular fa-arrows-rotate" />
      </ng-template>
    </soft-ds-button>

    <soft-ds-split-button
      variant="primary"
      size="md"
      icon="fa-regular fa-plus"
      [label]="('#PMS_ROSTER.NEW_ENTRY' | translate) ?? ''"
      [tooltip]="('#PMS_ROSTER.NEW_ENTRY' | translate) ?? ''"
      (execute)="service.handleContextMenuAction('createEmpty')"
      (optionSelected)="onNewEntryOption($event)"
      data-testid="softline-roster-new-entry"
    >
      <soft-ds-split-button-option
        value="createPlanEntry"
        icon="fa-regular fa-database"
        [label]="('#PMS_ROSTER.PLAN_ENTRY' | translate) ?? ''"
      />
      <soft-ds-split-button-option
        value="createAppointment"
        icon="fa-regular fa-calendar"
        [label]="('#PMS_ROSTER.APPOINTMENT' | translate) ?? ''"
      />
    </soft-ds-split-button>
  </div>
</ng-template>

<ng-template #viewLeadingMenuBar>
  <div class="flex items-center gap-2 h-full">
    <soft-ds-select
      size="md"
      [value]="hostComponent.calendarViewType()"
      (valueChange)="$event && hostComponent.calendarViewType.set($event)"
      leadingIcon="fa-regular fa-calendar-range"
      [listboxTitle]="('#PMS_ROSTER.VIEW_TYPE' | translate) ?? ''"
      minWidth="160px"
      data-testid="softline-roster-view-type"
    >
      @for (opt of viewTypeOptions(); track opt.v) {
        <soft-ds-select-option [value]="opt.v" [label]="opt.l" />
      }
    </soft-ds-select>

    <soft-ds-select
      size="md"
      [value]="hostComponent.calendarColumnWidth()"
      (valueChange)="$event && hostComponent.calendarColumnWidth.set($event)"
      leadingIcon="fa-regular fa-arrows-left-right"
      [listboxTitle]="('#PMS_ROSTER.COLUMN_WIDTH' | translate) ?? ''"
      minWidth="140px"
      data-testid="softline-roster-column-width"
    >
      @for (opt of columnWidthOptions(); track opt.v) {
        <soft-ds-select-option [value]="opt.v" [label]="opt.l" />
      }
    </soft-ds-select>

    <soft-ds-multi-select
      size="md"
      [value]="selectedEventTypeFilter()"
      (valueChange)="setEventTypes($event)"
      leadingIcon="fa-regular fa-filter"
      [placeholder]="('#PMS_ROSTER.EVENT_TYPES' | translate) ?? ''"
      [listboxTitle]="('#PMS_ROSTER.EVENT_TYPES' | translate) ?? ''"
      [showSelectionCount]="true"
      minWidth="180px"
      data-testid="softline-roster-event-types"
    >
      @for (opt of eventTypeOptions(); track opt.v) {
        <soft-ds-select-option [value]="opt.v" [label]="opt.l" />
      }
    </soft-ds-multi-select>
  </div>
</ng-template>

<ng-template #viewTrailingMenuBar>
  <soft-ds-toggle-group
    size="md"
    [value]="hostComponent.calendarDetails()"
    (valueChange)="setDetailOption($event)"
    [attr.aria-label]="('#PMS_ROSTER.DETAILS' | translate) ?? ''"
    data-testid="softline-roster-details"
  >
    @for (opt of detailPillOptions(); track opt.value) {
      <soft-ds-toggle [value]="opt.value" [label]="opt.label" [icon]="opt.icon" />
    }
  </soft-ds-toggle-group>
</ng-template>

<ng-template #selectionLeadingMenuBar>
  <div class="flex items-center gap-2 h-full">
    @if (!multiSelect()) {
      <soft-ds-button
        variant="primary"
        size="md"
        (click)="service.handleContextMenuAction('edit')"
        data-testid="softline-roster-selection-edit"
      >
        <ng-template softSlot="leading">
          <soft-ds-icon name="fa-regular fa-pencil" />
        </ng-template>
        {{ '#PMS_ROSTER.EDIT' | translate }}
      </soft-ds-button>
    }

    @if (!isAppointment()) {
      <soft-ds-button
        variant="outline"
        size="md"
        (click)="service.handleContextMenuAction('duplicate')"
        data-testid="softline-roster-selection-duplicate"
      >
        <ng-template softSlot="leading">
          <soft-ds-icon name="fa-regular fa-clone" />
        </ng-template>
        {{ '#PMS_ROSTER.DUPLICATE' | translate }}
      </soft-ds-button>
      <soft-ds-button
        variant="outline"
        size="md"
        (click)="service.handleContextMenuAction('copy')"
        data-testid="softline-roster-selection-copy"
      >
        <ng-template softSlot="leading">
          <soft-ds-icon name="fa-regular fa-copy" />
        </ng-template>
        {{ '#PMS_ROSTER.COPY' | translate }}
      </soft-ds-button>
    }

    @if (showTicketEventOptions()) {
      <soft-ds-button
        variant="outline"
        size="md"
        (click)="service.handleContextMenuAction('copyTicketKey')"
        data-testid="softline-roster-selection-copy-ticket"
      >
        <ng-template softSlot="leading">
          <soft-ds-icon name="fa-regular fa-copy" />
        </ng-template>
        {{ '#PMS_ROSTER.COPY_TICKET_NR' | translate }}
      </soft-ds-button>
      <soft-ds-button
        variant="outline"
        size="md"
        (click)="service.handleContextMenuAction('openTicket')"
        data-testid="softline-roster-selection-open-ticket"
      >
        <ng-template softSlot="leading">
          <soft-ds-icon name="fa-regular fa-up-right-from-square" />
        </ng-template>
        {{ '#PMS_ROSTER.OPEN_TICKET' | translate }}
      </soft-ds-button>
    }

    @if (showProjectEventOptions()) {
      <soft-ds-button
        variant="outline"
        size="md"
        (click)="service.handleContextMenuAction('copyProjectKey')"
        data-testid="softline-roster-selection-copy-object"
      >
        <ng-template softSlot="leading">
          <soft-ds-icon name="fa-regular fa-copy" />
        </ng-template>
        {{ '#PMS_ROSTER.COPY_OBJECT_NR' | translate }}
      </soft-ds-button>
    }

    @if (!isAppointment() && !multiSelect()) {
      <span #statusAnchor class="inline-flex">
        <soft-ds-button
          variant="outline"
          size="md"
          (click)="openStatusMenu(statusAnchor)"
          data-testid="softline-roster-selection-status"
        >
          <ng-template softSlot="leading">
            <soft-ds-icon name="fa-regular fa-circle" />
          </ng-template>
          {{ '#PMS_ROSTER.CHANGE_STATUS' | translate }}
          <ng-template softSlot="trailing">
            <soft-ds-icon name="fa-regular fa-chevron-down" />
          </ng-template>
        </soft-ds-button>
      </span>
    }

    @if (!isAppointment()) {
      <soft-ds-button
        variant="destructive"
        size="md"
        (click)="service.handleContextMenuAction('delete')"
        data-testid="softline-roster-selection-delete"
      >
        <ng-template softSlot="leading">
          <soft-ds-icon name="fa-regular fa-trash-can" />
        </ng-template>
        {{ '#PMS_ROSTER.DELETE' | translate }}
      </soft-ds-button>
    }
  </div>
</ng-template>

<ng-template #selectionTrailingMenuBar>
  <span class="text-xs text-foreground/70 px-1">
    <span class="font-semibold">{{ selectedEvents().length }}</span>
    {{ '#PMS_ROSTER.SELECTED' | translate }}
  </span>
</ng-template>

<ng-template #eventMenu>
  <soft-ds-context-menu>
    @if (!multiSelect()) {
      <soft-ds-context-menu-item
        icon="fa-regular fa-pen"
        label="Editieren"
        (triggered)="service.handleContextMenuAction('edit')"
      />
    }

    @if (!isAppointment()) {
      @if (!multiSelect()) {
        <soft-ds-context-menu-divider />
      }
      <soft-ds-context-menu-item
        icon="fa-regular fa-clone"
        label="Duplizieren"
        (triggered)="service.handleContextMenuAction('duplicate')"
      />
      <soft-ds-context-menu-item
        icon="fa-regular fa-copy"
        label="Kopieren"
        [keyboardShortcut]="[modifierKey, 'C']"
        (triggered)="service.handleContextMenuAction('copy')"
      />
      <soft-ds-context-menu-item
        icon="fa-regular fa-arrow-right"
        label="Auf nä. Montag kopieren"
        (triggered)="service.handleContextMenuAction('copyToNextMonday')"
      />
    }

    @if (!multiSelect() && (isTicket() || isProject())) {
      <soft-ds-context-menu-divider />
      @if (isTicket()) {
        <soft-ds-context-menu-item
          icon="fa-regular fa-copy"
          label="Ticket-Nr. kopieren"
          [submenu]="ticketCopySubmenu"
          splitSubmenu
          (triggered)="service.handleContextMenuAction('copyTicketKey')"
        />
        <soft-ds-context-menu-item
          icon="fa-regular fa-up-right-from-square"
          label="Ticket öffnen"
          (triggered)="service.handleContextMenuAction('openTicket')"
        />
      }
      @if (isProject()) {
        <soft-ds-context-menu-item
          icon="fa-regular fa-copy"
          label="Objekt-Nr. kopieren"
          (triggered)="service.handleContextMenuAction('copyProjectKey')"
        />
      }
    }

    @if (!isAppointment()) {
      <soft-ds-context-menu-divider />
      <soft-ds-context-menu-item
        icon="fa-regular fa-circle"
        label="Status ändern"
        [submenu]="statusSubmenu"
      />

      <soft-ds-context-menu-divider />
      <soft-ds-context-menu-item
        icon="fa-regular fa-trash-can"
        label="Löschen"
        variant="danger"
        (triggered)="service.handleContextMenuAction('delete')"
      />
    }
  </soft-ds-context-menu>
</ng-template>

<ng-template #ticketCopySubmenu>
  <soft-ds-context-menu>
    <soft-ds-context-menu-item
      icon="fa-regular fa-clipboard"
      label="Nr. - Name kopieren"
      (triggered)="service.handleContextMenuAction('copyTicketKeyAndName')"
    />
  </soft-ds-context-menu>
</ng-template>

<ng-template #statusSubmenu>
  <soft-ds-context-menu>
    <soft-ds-context-menu-item
      icon="fa-regular fa-circle"
      label="Undefiniert"
      (triggered)="service.handleContextMenuAction('changeStatus:UNDEFINED')"
    />
    <soft-ds-context-menu-item
      icon="fa-regular fa-circle-check"
      label="Erledigt"
      (triggered)="service.handleContextMenuAction('changeStatus:DONE')"
    />
    <soft-ds-context-menu-item
      icon="fa-regular fa-share"
      label="Weitergeleitet"
      (triggered)="service.handleContextMenuAction('changeStatus:HANDOVER_TO_OTHERS')"
    />
  </soft-ds-context-menu>
</ng-template>

<ng-template #calendarMenu>
  <soft-ds-context-menu>
    <soft-ds-context-menu-item
      icon="fa-regular fa-calendar-plus"
      label="Neuer Eintrag"
      (triggered)="service.handleContextMenuAction('create')"
    />
    @if (hasClipboardEvent()) {
      <soft-ds-context-menu-item
        icon="fa-regular fa-paste"
        label="Einfügen"
        [keyboardShortcut]="[modifierKey, 'V']"
        (triggered)="service.handleContextMenuAction('paste')"
      />
    }
  </soft-ds-context-menu>
</ng-template>
